import { HubConnectionBuilder, HubConnectionState } from "@microsoft/signalr";

// Singleton SignalR connection for the AI chat hub. Kept in the main bundle (like deviceHub) so
// @microsoft/signalr is never bundled into the lazily-loaded chat drawer chunk. Started lazily the
// first time the chat panel is opened (not auto-started for every page).
class ChatHub {
    constructor() {
        this.client = new HubConnectionBuilder()
            .withUrl("/chat-hub")
            // Recover automatically from transient drops instead of surfacing a hard error and
            // leaving the chat dead until a full page reload.
            .withAutomaticReconnect([0, 2000, 5000, 10000, 15000, 30000])
            .build();

        // AI turns stream over a long time with gaps between messages (a slow tool call, the model
        // "thinking"). The default 30s server timeout is too aggressive for that and trips
        // "Server timeout elapsed without receiving a message from the server", tearing down the
        // connection mid-chat. Give generous headroom over the server keep-alive interval (15s,
        // configured in AddSignalR server-side).
        this.client.serverTimeoutInMilliseconds = 120000; // 2 min
        this.client.keepAliveIntervalInMilliseconds = 15000;

        this.startPromise = null;

        // If the connection is permanently closed (auto-reconnect exhausted its retries, or it was
        // never able to start), forget the cached start promise so the next start() truly reconnects.
        this.client.onclose(() => {
            this.startPromise = null;
        });
    }

    // Returns a promise that resolves once the connection is usable. Safe to call repeatedly.
    start() {
        if (this.client.state === HubConnectionState.Connected) {
            return Promise.resolve();
        }
        // Connecting / Reconnecting / Disconnecting: don't call start() again (it would throw);
        // reuse the in-flight attempt if we have one, otherwise let the caller proceed and rely on
        // automatic reconnect to restore the connection.
        if (this.client.state !== HubConnectionState.Disconnected) {
            return this.startPromise || Promise.resolve();
        }
        if (!this.startPromise) {
            this.startPromise = this.client.start().catch((e) => {
                this.startPromise = null;
                throw e;
            });
        }
        return this.startPromise;
    }
}

export default new ChatHub();
